import Image from 'next/image';
import React, { useState, useEffect, useMemo } from 'react';
import { replaceImageUrl } from '../../utils/helpers/imageUtils';

/**
 * OptimizedImage Component
 * Serves images optimized through Next.js Image API with WebP/AVIF support
 * Supports different images for mobile/desktop via client-side detection
 *
 * @param {Object} props
 * @param {String} props.src - Image source URL (desktop by default, or single image)
 * @param {String} props.mobileSrc - Mobile-specific image URL (optional, for different mobile images)
 * @param {String} props.alt - Alt text for accessibility (required)
 * @param {Number} props.width - Image width (required unless using fill)
 * @param {Number} props.height - Image height (required unless using fill)
 * @param {String} props.className - CSS class name
 * @param {Object} props.style - Inline styles
 * @param {String} props.objectFit - Object fit property (cover, contain, fill)
 * @param {Boolean} props.priority - Whether to preload the image (use for LCP images)
 * @param {Number} props.quality - Image quality (1-100, default 85)
 * @param {String} props.sizes - Responsive sizes attribute for srcset optimization
 * @param {Boolean} props.responsive - Enable mobile/desktop image switching (default false)
 * @param {Number} props.mobileBreakpoint - Breakpoint for mobile detection (default 768)
 * @param {String} props.overrideSrc - Override the src attribute in the img tag (Next.js 14.2+, useful for SEO)
 */

const OptimizedImage = React.memo(({
    src,
    mobileSrc,
    alt,
    width,
    height,
    className = '',
    style = {},
    objectFit = 'cover',
    priority = false,
    quality,
    sizes,
    responsive = false,
    mobileBreakpoint = 768,
    // overrideSrc,
    ...props
}) => {
    // Smart defaults based on image dimensions
    const defaultQuality = useMemo(() => {
        if (quality !== undefined) return quality;
        // Smaller images need less quality to save bandwidth (ultra-aggressive compression)
        if (width <= 50 || height <= 50) return 55; // Tiny icons - extreme compression
        if (width <= 100 || height <= 100) return 40; // Small logos/icons - ULTRA aggressive (minimum)
        if (width <= 300 || height <= 300) return 70; // Medium images - moderately compressed
        if (width <= 500 || height <= 500) return 80; // Medium-large images
        return 85; // Large hero images only
    }, [quality, width, height]);

    const defaultSizes = useMemo(() => {
        if (sizes) return sizes;
        // Calculate sizes based on width to prevent oversized downloads
        if (width <= 100) return '100px';
        if (width <= 200) return '200px';
        if (width <= 300) return '300px';
        if (width <= 500) return '(max-width: 768px) 100vw, 500px';
        // For large images, use responsive sizes
        return '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw';
    }, [sizes, width]);

    // Client-side mobile detection for responsive images
    const [isMobile, setIsMobile] = useState(null);
    const [mounted, setMounted] = useState(false);

    // Process image URLs with memoization to prevent unnecessary recalculations
    const processedSrc = useMemo(() => replaceImageUrl(src), [src]);
    const processedMobileSrc = useMemo(() => 
        mobileSrc ? replaceImageUrl(mobileSrc) : null, 
        [mobileSrc]
    );

    // Determine which image to show based on viewport
    const activeImageSrc = useMemo(() => {
        if (!responsive || !processedMobileSrc) {
            return processedSrc;
        }
        // During SSR or before mount, default to desktop to prevent hydration mismatch
        if (isMobile === null) {
            return processedSrc;
        }
        return isMobile ? processedMobileSrc : processedSrc;
    }, [responsive, processedMobileSrc, processedSrc, isMobile]);

    // Memoize image styles to prevent style object recreation
    const imageStyle = useMemo(() => ({
        objectFit,
        ...style,
    }), [objectFit, style]);

    // Handle client-side mobile detection
    useEffect(() => {
        if (!responsive || !mobileSrc) {
            return;
        }

        setMounted(true);

        const checkMobile = () => {
            setIsMobile(window.innerWidth <= mobileBreakpoint);
        };

        // Initial check
        checkMobile();

        // Listen for resize with debouncing for performance
        let timeoutId;
        const handleResize = () => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(checkMobile, 150);
        };

        window.addEventListener('resize', handleResize, { passive: true });

        return () => {
            window.removeEventListener('resize', handleResize);
            clearTimeout(timeoutId);
        };
    }, [responsive, mobileSrc, mobileBreakpoint]);

    // Render Next.js optimized image
    return (
        <Image
            src={activeImageSrc}
            alt={alt || 'Image'}
            width={width}
            height={height}
            className={className}
            style={imageStyle}
            priority={priority}
            quality={defaultQuality}
            sizes={defaultSizes}
            loading={priority ? 'eager' : 'lazy'}
            overrideSrc={activeImageSrc}
            {...props}
        />
    );
});

OptimizedImage.displayName = 'OptimizedImage';

export default OptimizedImage;
